import {acceptHMRUpdate, defineStore} from "pinia";
import {computed, ref} from "vue";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import {
    TreatmentLine,
    TreatmentLineDosageDraft,
    TreatmentLineDosagePayload,
    TreatmentSelectionsView
} from "@/medication-checkup/interfaces/treatment-selections";
import LinePosologies, {LinePosology} from "@/medication-checkup/services/LinePosologies";
import TreatmentLineDosageService from "@/medication-checkup/services/TreatmentLineDosageService";

/**
 * The review of the treatments while it is live: the lines checked and the
 * posologies saved, by line id — local until the closing action submits
 * them with the selection.
 *
 * It lives beside the scene rather than in it because the checklist of the
 * step reads it too, and edits from it. It belongs to one block at a time,
 * the one whose scene is playing; coming back to the scene re-runs the scan,
 * which seeds it again, so it starts over with every live visit.
 */
export const useTreatmentSelectionsStore = defineStore('medication-checkup-treatment-selections', () => {
    const checkup = useMedicationCheckupStore();

    /**
     * The block under review — null when no scene of the treatment
     * selections is playing.
     */
    const blockId = ref<number | null>(null);

    const checked = ref<number[]>([]);

    const drafts = ref<Record<number, TreatmentLineDosageDraft>>({});

    /**
     * The line whose posology is open in the dialog, by id: an action of the
     * review refreshes the served lines, so an object held here would go
     * stale.
     */
    const editingId = ref<number | null>(null);

    const lines = computed<TreatmentLine[]>(() => {
        const block = checkup.displayedBlocks.find(candidate => candidate.id === blockId.value);

        return (block?.view as TreatmentSelectionsView | null | undefined)?.lines ?? [];
    });

    /**
     * The selection, less anything the served lines no longer carry.
     *
     * A re-scan drops collected lines whose product was not dispensed any
     * more, so a checked id can outlive its line. Submitting one makes the
     * server refuse the whole selection, hence the filter on the way out.
     */
    const checkedIds = computed(() => new Set(checked.value));

    /**
     * The hand-added lines being deleted: out of the review at once, back if
     * the server refuses — a closing sent meanwhile must not name them.
     */
    const removing = ref<number[]>([]);

    const retained = computed<TreatmentLine[]>(() =>
        lines.value.filter(line => checkedIds.value.has(line.id) && !removing.value.includes(line.id))
    );

    const editing = computed<TreatmentLine | null>(() =>
        lines.value.find(line => line.id === editingId.value) ?? null
    );

    /**
     * The draft saved for the line being edited, if any.
     */
    const editingDraft = computed<TreatmentLineDosageDraft | null>(() =>
        editing.value ? drafts.value[editing.value.id] ?? null : null
    );

    const isChecked = (line: TreatmentLine): boolean => checkedIds.value.has(line.id);

    /**
     * The posology of every line, by id — read and validated once per change
     * of the lines or the drafts, not at every render of every place showing
     * the line.
     */
    const posologies = computed(() => new Map<number, LinePosology>(
        lines.value.map(line => [line.id, LinePosologies.live(line, drafts.value[line.id] ?? null)])
    ));

    const posology = (line: TreatmentLine): LinePosology => {
        return posologies.value.get(line.id) ?? LinePosologies.live(line, drafts.value[line.id] ?? null);
    };

    /**
     * The retained lines that still ask for their posology.
     */
    const missing = computed<TreatmentLine[]>(() => retained.value.filter(line => posology(line).draft === null));

    /**
     * Start the review of a block over, with nothing checked or saved.
     */
    const start = (id: number): void => {
        blockId.value = id;
        checked.value = [];
        drafts.value = {};
        editingId.value = null;
        removing.value = [];
    };

    /**
     * Let go of a block whose scene stops playing — a review starting over
     * on another block keeps its own.
     */
    const stop = (id: number): void => {
        if (blockId.value === id) {
            blockId.value = null;
            editingId.value = null;
        }
    };

    /**
     * Check lines on top of whatever is already checked.
     */
    const check = (ids: number[]): void => {
        checked.value = [...new Set([...checked.value, ...ids])];
    };

    const toggle = (line: TreatmentLine): void => {
        checked.value = isChecked(line)
            ? checked.value.filter(id => id !== line.id)
            : [...checked.value, line.id];
    };

    const startRemoving = (line: TreatmentLine): void => {
        removing.value = [...removing.value, line.id];
    };

    const stopRemoving = (line: TreatmentLine): void => {
        removing.value = removing.value.filter(id => id !== line.id);
    };

    const forget = (line: TreatmentLine): void => {
        checked.value = checked.value.filter(id => id !== line.id);
        delete drafts.value[line.id];
    };

    const edit = (line: TreatmentLine): void => {
        editingId.value = line.id;
    };

    const stopEditing = (): void => {
        editingId.value = null;
    };

    /**
     * Keep the posology saved for the line being edited. Saving a posology
     * is reviewing the line with the patient, so it gets checked.
     */
    const keep = (draft: TreatmentLineDosageDraft): void => {
        const line = editing.value;

        if (line) {
            drafts.value[line.id] = draft;
            check([line.id]);
        }

        editingId.value = null;
    };

    /**
     * The selection as the closing action carries it: the retained lines,
     * and the posology of each one that has one — saved, or read and shown.
     */
    const payload = (): {line_ids: number[]; dosages: TreatmentLineDosagePayload[]} => ({
        line_ids: retained.value.map(line => line.id),
        dosages: retained.value.flatMap(line => {
            const draft = posology(line).draft;

            return draft ? [TreatmentLineDosageService.toPayload(line, draft)] : [];
        })
    });

    return {
        blockId,
        retained,
        removing,
        editing,
        editingDraft,
        missing,
        isChecked,
        posology,
        start,
        stop,
        check,
        toggle,
        startRemoving,
        stopRemoving,
        forget,
        edit,
        stopEditing,
        keep,
        payload
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTreatmentSelectionsStore, import.meta.hot));
}
